import React, { useContext, useEffect, useState } from 'react'
import { Sidebar } from './sidebar'
import { Topbar } from './topbar'
import { Projects } from './projects'
import { Events } from './events'
import { Team } from './team'
import Products from './products'
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom'
import toast from 'react-hot-toast'
import { useCustomFetch } from '../../hooks/useCustomFetch'
import { AuthContext } from '../../context/AuthContext'
import { DashboardContext } from '../../context/DashboardContext'
import { Meetings } from './meetings'
import { Recruiting } from './recruiting'
import { Integrations } from './integrations'
import { WelcomeDashboard } from './WelcomeDashboard'
import clsx from 'clsx'
import { RecentMeetings } from './RecentMeetings'
import { DynamicItems } from './dynamic-items/DynamicItems'
import { Chats } from './chats'
import Roadmap from './roadmap'
import FloatingActionButton from '@/components/askAnything/FloatingActionButton'
import { HomeNew } from './home-new'
import { Settings } from './team/settings'
import Competitors from './competitors'

export function Dashboard() {
  const [isLoading, setIsLoading] = useState(true)
  const [searchParams] = useSearchParams()
  const { selectedMenu } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useContext(AuthContext)
  const { refreshActiveWorkspace, activeWorkspace, upcomingMeetings, eventsLoading, projectsLoading } =
    useContext(DashboardContext)
  const { filteredProjects, allProjects } = useContext(DashboardContext)
  const setSelectedMenu = (newSelectedMenu) => navigate(`/dashboard/${newSelectedMenu}`)
  const [showLoader, setShowLoader] = useState(true)
  const limit = 10
  const customFetch = useCustomFetch()

  // Check if current page is a roadmap page, using the location pathname
  const isRoadmapPage = location.pathname.startsWith('/dashboard/roadmap')
  const isHomeNew = selectedMenu === 'home-new'

  const handleSubscribeToPlan = async (workspaceId, planId) => {
    try {
      const response = await customFetch('/createCheckoutUrl', 'POST', { workspaceId, planId })
      if (!response.hostedPageUrl) throw new Error('No plan URL found')
      window.location.href = response.hostedPageUrl
    } catch (error) {
      console.error('Error initiating payment', error)
    }
  }

  useEffect(() => {
    if (isLoading || eventsLoading) return
    ;(async function () {
      await new Promise((resolve) => setTimeout(resolve, 1000))
      setShowLoader(false)
    })()
  }, [filteredProjects, upcomingMeetings, eventsLoading, isLoading])

  useEffect(() => {
    if (typeof user !== 'object' || Object.keys(user).length === 0) return
    if (!activeWorkspace) return
    const paymentStatus = searchParams.get('payment_status')
    if (paymentStatus === 'success') return
    const planId = searchParams.get('plan_id')
    if (!planId) return
    const workspacesAmount = Object.keys(user.workspaces).length
    if (workspacesAmount !== 1) return
    if (activeWorkspace.plan_id) {
      navigate(`/workspace/select?plan_id=${planId}`)
    } else {
      handleSubscribeToPlan(Object.keys(user.workspaces)[0], planId)
    }
  }, [activeWorkspace])

  useEffect(() => {
    if (typeof user !== 'object' || Object.keys(user).length === 0) return
    // check if this is a redirect from the payment gateway
    const paymentStatus = searchParams.get('payment_status')
    const planId = searchParams.get('plan_id')
    if (paymentStatus === 'success') {
      toast.success('Payment successful, might take a few minutes to reflect in your account')
      setTimeout(refreshActiveWorkspace, 30000)
      return
    }
    if (planId) {
      const workspacesAmount = Object.keys(user.workspaces).length
      if (workspacesAmount === 0) {
        navigate(`/workspace/create?plan_id=${planId}`)
      } else if (workspacesAmount > 1) {
        navigate(`/workspace/select?plan_id=${planId}`)
      }
    }
  }, [user.workspaces])

  useEffect(() => {
    const paymentStatus = searchParams.get('payment_status')
    const planId = searchParams.get('plan_id')
    if (paymentStatus === 'success' || !planId) {
      setIsLoading(false)
    }
  }, [searchParams])

  return (
    <div className="flex h-screen bg-white dark:bg-gray-900">
      <div className="h-full max-w-[250px] w-full flex-shrink-0 border-r-2 border-grey-200 dark:border-gray-700 overflow-y-auto hideScrollbar">
        <Sidebar selectedMenu={selectedMenu} setSelectedMenu={setSelectedMenu} />
      </div>
      <div className="flex flex-1 flex-col overflow-hidden">
        {!isRoadmapPage && (
          <div className="h-[70px] flex-shrink-0 border-b-2 border-grey-200 dark:border-gray-700 z-10">
            <Topbar />
          </div>
        )}

        <div
          className={clsx(
            'flex-1 hideScrollbar z-0 h-[calc(100vh-var(--topbar-height))]',
            !isRoadmapPage &&
              `${!isHomeNew ? 'xl:pl-10 sm:pl-4 pb-10 overflow-y-auto' : ''} border-b-2 border-grey-200 dark:border-gray-700 bg-grey-100 dark:bg-gray-800`,
            (!!allProjects.length || !!upcomingMeetings.length) &&
              selectedMenu === 'home' &&
              'grid grid-cols-1 md:grid-cols-6 gap-4'
          )}
          style={{
            paddingLeft: !allProjects.length && !upcomingMeetings.length && selectedMenu === 'home' ? '0px' : undefined,
          }}
        >
          <div className={clsx('h-full', !isRoadmapPage && 'md:col-span-6 xl:col-span-4')}>
            {selectedMenu === 'home' && (
              <>
                {!!allProjects.length || !!upcomingMeetings.length ? (
                  <>
                    <Events limit={limit} events={upcomingMeetings} eventsLoading={eventsLoading} />
                    <RecentMeetings setSelectedMenu={setSelectedMenu} />
                    <Projects limit={limit} />
                  </>
                ) : (
                  <WelcomeDashboard />
                )}
              </>
            )}
            {selectedMenu === 'home-new' && <HomeNew />}
            {selectedMenu === 'projects' && <Projects />}
            {selectedMenu === 'meetings' && <Meetings />}
            {selectedMenu === 'competitors' && <Competitors />}
            {selectedMenu === 'recruiting' && <Recruiting />}
            {selectedMenu === 'integrations' && <Integrations />}
            {selectedMenu === 'chats' && <Chats />}
            {selectedMenu === 'team' && <Team />}
            {selectedMenu === 'team-settings' && <Settings />}
            {selectedMenu === 'products' && <Products />}
            {selectedMenu === 'roadmap' && <Roadmap />}
          </div>
          {selectedMenu === 'home' && (!!allProjects.length || !!upcomingMeetings.length) && (
            <div className="md:col-span-6 xl:col-span-2">
              <DynamicItems vertical={!!allProjects.length || !!upcomingMeetings.length} padding={null}></DynamicItems>
            </div>
          )}
        </div>
      </div>

      <FloatingActionButton />
    </div>
  )
}
